import React, { useState, useEffect } from "react";
import axios from "axios";
import "../css/assignRoutes.css";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "./CustomButton";

const AssignRoutes = () => {
  const [formData, setFormData] = useState({
    route_name: [""],
    route_type: [""],
    distance_from_hq: [0],
    tp_visit_cycle_per_month: [0],
    headquarter: "",
  });

  const [states, setStates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [showTable, setShowTable] = useState(false);
  const [headquarters, setHeadquarters] = useState([]);
  const [routeTypes, setRouteTypes] = useState([]);
  const [employeeHeadquarter, setEmployeeHeadquarter] = useState("");
  const [distance, setDistance] = useState(Array(26).fill(""));
  const [visitCycle, setVisitCycle] = useState(Array(26).fill(""));
  const [previousRoutes, setPreviousRoutes] = useState([]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/states`, {
        headers: getAuthHeaders(),
      })
      .then((res) => setStates(res.data))
      .catch((err) => console.error("Error fetching states:", err));
    axios
      .get(`${BASE_URL}/api/headquarters`, {
        headers: getAuthHeaders(),
      })
      .then((res) => setHeadquarters(res.data))
      .catch((err) => console.error("Error fetching headquarters:", err));

    axios
      .get(`${BASE_URL}/api/route_types`, {
        headers: getAuthHeaders(),
      })
      .then((res) => setRouteTypes(res.data))
      .catch((err) => console.error("Error fetching route types:", err));
  }, []);

  const handleStateChange = (stateName) => {
    setSelectedState(stateName);
    setSelectedEmployee("");
    setEmployees([]);
    setShowTable(false);

    const selectedStateObj = states.find((state) => state.name === stateName);
    if (!selectedStateObj) return;

    axios
      .get(`${BASE_URL}/api/employees?state_id=${selectedStateObj.id}`, {
        headers: getAuthHeaders(),
      })
      .then((res) => setEmployees(res.data))
      .catch((err) => console.error("Error fetching employees:", err));
  };

  const handleEmployeeChange = (employeeId) => {
    setSelectedEmployee(employeeId);
    setHeadquarters([]); // Clear previous headquarters
    setDistance(Array(26).fill("")); // Reset distance inputs
    setVisitCycle(Array(26).fill("")); // Reset visit cycle inputs
    setFormData({
      route_name: Array(26).fill(""),
      route_type: Array(26).fill(""),
      distance_from_hq: Array(26).fill(0),
      tp_visit_cycle_per_month: Array(26).fill(0),
      headquarter: "",
    });
    setPreviousRoutes(employeeId);

    if (!employeeId) return;

    axios
      .get(`${BASE_URL}/api/employees/${employeeId}`, {
        headers: getAuthHeaders(),
      })
      .then((res) => {
        // Update any necessary state
      })
      .catch((err) => console.error("Error fetching employee:", err));

    axios
      .get(`${BASE_URL}/api/headquarters/employee/${employeeId}`, {
        headers: getAuthHeaders(),
      })
      .then((res) => setHeadquarters(res.data))
      .catch((err) => console.error("Error fetching headquarters:", err));
  };

  const handleGoClick = () => {
    if (!selectedState || !selectedEmployee) {
      alert("Please select State and Employee.");
      return;
    }

    axios
      .get(`${BASE_URL}/api/headquarters/employee/${selectedEmployee}`, {
        headers: getAuthHeaders(),
      })
      .then((res) => {
        console.log("API Response for HQ:", res.data);
        if (Array.isArray(res.data) && res.data.length > 0) {
          setHeadquarters(res.data);
          setFormData((prev) => ({
            ...prev,
            headquarter: res.data[0].id,
          }));
          fetchPreviousRoutes(selectedEmployee);
        } else {
          setHeadquarters([]);
          setFormData((prev) => ({ ...prev, headquarter: "" }));
        }
      })
      .catch((err) => console.error("Error fetching headquarters:", err));

    setShowTable(true);
  };

  const handleInputChange = (e, index, field) => {
    const value = e.target.value;

    if (field === "distance") {
      // Validate maximum 3 digits
      if (value.length > 3) return;
      if (value < 0) return;
      if (value > 999) return;

      const updatedDistance = [...distance];
      updatedDistance[index] = value;
      setDistance(updatedDistance);
    } else if (field === "visitCycle") {
      const updatedVisitCycle = [...visitCycle];
      updatedVisitCycle[index] = value;
      setVisitCycle(updatedVisitCycle);
    }
  };

  // Handle input changes
  const handleChange = (e, index) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: prev[name]
        ? [...prev[name].slice(0, index), value, ...prev[name].slice(index + 1)]
        : Array(26).fill(""),
    }));
  };

  // ✅ Submit Data to API
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedEmployee) {
      console.error("❌ No employee selected");
      alert("Please select an employee first");
      return;
    }

    const selectedEmpData = employees.find(
      (emp) => emp.id === parseInt(selectedEmployee, 10)
    );

    if (!selectedEmpData) {
      console.error("❌ Employee data not found for ID:", selectedEmployee);
      alert("Employee data not found. Please refresh and try again.");
      return;
    }

    const filteredRoutes = formData.route_name
      .map((name, index) => ({
        route_name: name,
        route_type_id: parseInt(formData.route_type[index], 10),
        distance_from_hq: parseFloat(distance[index]) || 0,
        tp_visit_cycle_per_month: parseInt(visitCycle[index], 10) || 0,
        headquarter_id: formData.headquarter,
        added_date: new Date().toISOString().split("T")[0],
        employee_id: selectedEmpData.id,
        state_id: selectedEmpData.state_id,
        city_id: selectedEmpData.city_id,
        division_id: selectedEmpData.division_id,
        active: true,
        no_of_doctors: null,
        no_of_chemist: null,
        no_of_stockist: null,
      }))
      .filter((route) => route.route_name.trim() !== "");

    if (filteredRoutes.length === 0) {
      console.warn("No valid routes to submit.");
      return;
    }

    console.log("🚀 Submitting Data:", JSON.stringify(filteredRoutes, null, 2));

    try {
      const response = await axios.post(
        `${BASE_URL}/api/routes`,
        filteredRoutes,
        {
          headers: getAuthHeaders(),
        }
      );
      console.log("✅ Routes saved:", response.data);
      alert("Routes added successfully!");
      setFormData({
        route_name: Array(26).fill(""),
        route_type: Array(26).fill(""),
        distance_from_hq: Array(26).fill(0),
        tp_visit_cycle_per_month: Array(26).fill(0),
        headquarter: "",
      });
      setDistance(Array(26).fill(""));
      setVisitCycle(Array(26).fill(""));
    } catch (error) {
      console.error("❌ Error saving routes:", error.response?.data || error);
      alert("Failed to add routes.");
    }
  };

  const fetchPreviousRoutes = async (employeeId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/previous_routes/employee/${employeeId}`,
        {
          headers: getAuthHeaders(),
        }
      );

      const data = response?.data ?? {};
      console.log("API Response for previous route:", data);

      let routesData = [];

      if (Array.isArray(data)) {
        routesData = data;
      } else if (Array.isArray(data.routes)) {
        routesData = data.routes;
      } else if (Array.isArray(data.data)) {
        routesData = data.data;
      }

      const formattedRoutes = (routesData || []).map((route) => ({
        name: route?.route_name || "Unnamed Route",
        type: route?.route_type_name || "Unknown",
        distance: route?.distance_from_hq ?? "N/A",
      }));

      setPreviousRoutes(formattedRoutes);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        console.log(`No previous routes found for employee ${employeeId}.`);
      } else {
        console.error("Fetch error:", error.message);
      }
      setPreviousRoutes([]);
    }
  };

  return (
    <div className="assign-routes-container">
      <div className="p-2 border-2 border-gray-300 rounded-lg bg-white shadow-md">
        <h1 class="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span class="text-blue-700">Add </span>
          <span class="text-blue-500">Routes</span>
        </h1>

        <div className="assign-routes-filter-row">
          <label className="assign-routes-label">State</label>
          <select
            className="assign-routes-dropdown"
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
          >
            <option value="">Select State</option>
            {states.map((state) => (
              <option key={state.id} value={state.name}>
                {state.name}
              </option>
            ))}
          </select>

          <label className="assign-routes-label">Employee</label>
          <select
            className="assign-routes-dropdown"
            value={selectedEmployee}
            onChange={(e) => handleEmployeeChange(e.target.value)}
            disabled={!selectedState}
          >
            <option value="">Select Employee</option>
            {employees.length > 0 ? (
              employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.first_name} {emp.last_name} | {emp.employee} ({emp.designation} |{" "}
                  {emp.headquarter_name} | {emp.division})
                </option>
              ))
            ) : (
              <option value="" disabled>
                No employees found
              </option>
            )}
          </select>
        </div>

        <div className="flex justify-center">
          <CustomButton variant="go" onClick={handleGoClick}>
            GO
          </CustomButton>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {showTable && (
          <div className="p-2 border-2 border-gray-300 rounded-lg bg-white shadow-md mt-5">
            <div className="flex flex-row gap-4">
              <table className="routes-table unlock-dcr-leave-table">
                <thead className="text-white">
                  <tr>
                    <th>S.No</th>
                    <th>Route Name</th>
                    <th>Head Quarter</th>
                    <th>Route Type</th>
                    <th>
                      Distance<br></br>From km (One Way) (KM)
                    </th>
                    {/* <th>TP Visit Cycle<br></br> Per MOnth</th> */}
                  </tr>
                </thead>
                <tbody>
                  {[...Array(26)].map((_, index) => (
                    <tr key={index}>
                      <td className="px-4 py-2">{index + 1}</td>
                      <td className="px-4 py-4">
                        <input
                          type="text"
                          name="route_name"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors"
                          value={formData.route_name[index] || ""}
                          onChange={(e) => handleChange(e, index)}
                          placeholder="Enter route name"
                          required
                        />
                      </td>
                      <td className="px-4 py-2">
                        <select
                          name="headquarter"
                          value={formData.headquarter}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              headquarter: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors"
                          required
                        >
                          {headquarters.length > 0 ? (
                            <>
                              <option value="">Select Headquarter</option>
                              {headquarters.map((hq) => (
                                <option key={hq.id} value={hq.id}>
                                  {hq.name}
                                </option>
                              ))}
                            </>
                          ) : (
                            <option value="" disabled>
                              No Headquarters Found
                            </option>
                          )}
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <select
                          name="route_type"
                          value={formData.route_type[index] || ""}
                          onChange={(e) => handleChange(e, index)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors"
                        >
                          <option value="">Select Route Type</option>
                          {routeTypes.map((rt) => (
                            <option key={rt.id} value={rt.id}>
                              {rt.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors"
                          value={distance[index] || ""}
                          placeholder="0"
                          onChange={(e) =>
                            handleInputChange(e, index, "distance")
                          }
                          min="0"
                          max="999"
                          onInput={(e) => {
                            if (e.target.value.length > 3) {
                              e.target.value = e.target.value.slice(0, 3);
                            }
                          }}
                        />
                      </td>
                      {/* <td className="px-4 py-2">
                                        <input
                                            type="number"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors"
                                            value={visitCycle[index] || ""}
                                            placeholder="0"
                                            onChange={(e) => handleInputChange(e, index, "visitCycle")}
                                        />
                                    </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* ✅ Right Side: Assigned Routes (Correct Position) */}
              <div className="assigned-routes-container">
                <h1 class="assigned-routes-title text-xl sm:text-2xl font-bold text-center text-gray-800  whitespace-normal">
                  <span class="text-blue-700">Assigned Route: KM ONE WAY </span>
                </h1>
                <div className="assigned-routes-box">
                  {Array.isArray(previousRoutes) &&
                  previousRoutes.length > 0 ? (
                    <ul>
                      {previousRoutes.map((route, index) => (
                        <li key={`${index}-${route.name}`}>
                          {index + 1}) {route.name} ({route.type} |{" "}
                          {route.distance} KM)
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No assigned routes found.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="action-buttons">
              <button className="submit-btn" onClick={handleSubmit}>
                Submit
              </button>
              <button
                className="return-btn"
                onClick={() => setShowTable(false)}
              >
                Return
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default AssignRoutes;
